import { hasPostTag, postTagCounts, postTagKey, postTagLength } from "./post-tags";

// The native Posts page's search, sort and filters, read from its address so a reload or a
// shared link shows the same posts: ?q=, ?sort=oldest, ?type=<kind>, ?show=free|members,
// ?tag=<name>.

export const postFeedKinds = ["post", "gallery", "video", "audio", "download"] as const;
export type PostFeedKind = (typeof postFeedKinds)[number];
export const postFeedKindLabels: Record<PostFeedKind, string> = {
  post: "Posts",
  gallery: "Galleries",
  video: "Videos",
  audio: "Audio",
  download: "Downloads",
};
export const postFeedAudiences = ["free", "members"] as const;
export type PostFeedAudience = (typeof postFeedAudiences)[number];
export const postFeedAudienceLabels: Record<PostFeedAudience, string> = {
  free: "Free to read",
  members: "Members only",
};

export type PostFeedFilters = {
  q: string;
  sort: "newest" | "oldest";
  kind: PostFeedKind | "";
  show: PostFeedAudience | "";
  /** A tag's name as its tile links it, matched whatever its case. */
  tag: string;
};

type FeedItem = {
  title: string;
  description: string;
  excerpt?: string;
  type: "post" | "gallery" | "video" | "audio" | "download";
  mediaKind?: "video" | "audio" | null;
  access: "public" | "members";
  tags?: readonly string[];
};

const params = ["q", "sort", "type", "show", "tag"] as const;

/** Whether the address asks for a filtered view, which the native page always serves. */
export const isFilteredPostFeed = (search: URLSearchParams) =>
  params.some((name) => search.has(name));

export function postFeedFilters(search: URLSearchParams): PostFeedFilters {
  const kind = search.get("type");
  const show = search.get("show");
  return {
    q: (search.get("q") ?? "").trim().slice(0, 100),
    sort: search.get("sort") === "oldest" ? "oldest" : "newest",
    kind: postFeedKinds.includes(kind as PostFeedKind) ? (kind as PostFeedKind) : "",
    show: postFeedAudiences.includes(show as PostFeedAudience) ? (show as PostFeedAudience) : "",
    tag: (search.get("tag") ?? "").replace(/\s+/g, " ").trim().slice(0, postTagLength),
  };
}

/** What a post is, as a visitor would filter it: a post with a video is a video. */
export function postFeedKind(item: Pick<FeedItem, "type" | "mediaKind">): PostFeedKind {
  if (item.type === "gallery" || item.type === "download") return item.type;
  return item.mediaKind ?? (item.type === "video" || item.type === "audio" ? item.type : "post");
}

/** Posts in the order the feed received them (newest first), narrowed and sorted. */
export function filterPostFeed<T extends FeedItem>(items: T[], filters: PostFeedFilters): T[] {
  const query = filters.q.toLowerCase();
  const shown = items.filter(
    (item) =>
      (!filters.kind || postFeedKind(item) === filters.kind) &&
      (!filters.show || (filters.show === "members") === (item.access === "members")) &&
      (!filters.tag || hasPostTag(item, filters.tag)) &&
      (!query ||
        `${item.title} ${item.description} ${item.excerpt ?? ""} ${item.tags?.join(" ") ?? ""}`
          .toLowerCase()
          .includes(query)),
  );
  return filters.sort === "oldest" ? [...shown].reverse() : shown;
}

/**
 * The tag tiles above the feed: every tag its posts use with how many use it, the `shown` most
 * used as tiles and the rest for More. A chosen tag from the rest joins the tiles, so the tag
 * being shown is always in sight; `selected` is its entry, spelled as the posts spell it.
 */
export function postFeedTagTiles(items: readonly FeedItem[], tag: string, shown = 5) {
  const counts = postTagCounts(items);
  const selected = tag
    ? counts.find((entry) => postTagKey(entry.tag) === postTagKey(tag))
    : undefined;
  const tiles = counts.slice(0, shown);
  const promoted = selected && !tiles.includes(selected) ? selected : undefined;
  return {
    tiles: promoted ? [...tiles, promoted] : tiles,
    more: counts.slice(shown).filter((entry) => entry !== promoted),
    selected,
  };
}

/** The address of the feed with some choices changed; empty choices leave the address. */
export function postFeedHref(
  path: string,
  filters: PostFeedFilters,
  changes: Partial<PostFeedFilters> = {},
  preview = false,
) {
  const next = { ...filters, ...changes };
  const query = new URLSearchParams();
  if (next.q) query.set("q", next.q);
  if (next.sort === "oldest") query.set("sort", "oldest");
  if (next.kind) query.set("type", next.kind);
  if (next.show) query.set("show", next.show);
  if (next.tag) query.set("tag", next.tag);
  if (preview) query.set("preview", "1");
  const text = query.toString();
  return text ? `${path}?${text}` : path;
}
